/**
 * Build me.bubakii PWA URLs with consistent UTM defaults.
 * Naming aligns with marketing `bubakii` repo `src/lib/pwaLinks.js`.
 *
 * @see docs/ANALYTICS_UTM_AND_AUDIENCE.md
 */
import { getPwaPublicBaseUrl } from "./pwaPublicUrl.js";

/** @typedef {{ utm_source?: string, utm_medium?: string, utm_campaign?: string, utm_content?: string }} UtmParams */

/** Canonical UTM presets (lowercase, underscores, no PII) */
export const UTM_PRESETS = Object.freeze({
  WEBSITE_HANDOFF: Object.freeze({
    utm_source: "website",
    utm_medium: "cta",
    utm_campaign: "pwa_handoff",
  }),
  ADMIN_JOB_BOARD_SHARE: Object.freeze({
    utm_source: "admin",
    utm_medium: "share",
    utm_campaign: "job_board",
  }),
});

/**
 * Normalize a UTM value: lowercase, underscores; drop PII-looking values.
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeUtmValue(value) {
  if (value == null) return "";
  const raw = String(value).trim().toLowerCase().replace(/\s+/g, "_");
  if (!raw) return "";
  if (raw.includes("@") || /\d{7,}/.test(raw)) return "";
  return raw.replace(/[^a-z0-9_]+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
}

/**
 * @param {string} path - path starting with /
 * @param {UtmParams} [utm]
 * @param {string} [baseUrl] - override base (tests / SSR)
 * @returns {string | null} absolute URL, or null if base URL unavailable
 */
export function buildPwaUrl(path, utm = {}, baseUrl = getPwaPublicBaseUrl()) {
  if (!baseUrl) return null;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(normalizedPath, `${baseUrl.replace(/\/$/, "")}/`);
  const params = {
    utm_source: normalizeUtmValue(utm.utm_source) || UTM_PRESETS.WEBSITE_HANDOFF.utm_source,
    utm_medium: normalizeUtmValue(utm.utm_medium) || UTM_PRESETS.WEBSITE_HANDOFF.utm_medium,
    utm_campaign:
      normalizeUtmValue(utm.utm_campaign) || UTM_PRESETS.WEBSITE_HANDOFF.utm_campaign,
    ...(utm.utm_content
      ? { utm_content: normalizeUtmValue(utm.utm_content) }
      : {}),
  };
  Object.entries(params).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });
  return url.toString();
}

/**
 * Admin OS share link for a public job board detail page.
 * @param {string} publicJobId
 * @returns {string | null}
 */
export function buildAdminJobBoardShareUrl(publicJobId) {
  if (!publicJobId) return null;
  return buildPwaUrl(
    `/providers/jobs/${encodeURIComponent(String(publicJobId))}`,
    UTM_PRESETS.ADMIN_JOB_BOARD_SHARE
  );
}
